import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";

const AddServiceReminder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const server_url = localStorage.getItem("server_url");
  const company_id = localStorage.getItem("company_id") || "2";
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const grage_id = userData.grage_id || "1";

  // Check if we're editing an existing reminder
  const isEdit = location.pathname.includes("edit");
  const existingReminder = location.state?.reminder;

  // Searchable Dropdown Component
  const SearchableDropdown = ({
    options = [],
    value,
    onChange,
    placeholder,
    name,
    error,
    loading,
    disabled,
    required,
  }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState(options);
    const dropdownRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    }, [searchTerm, options]);

    useEffect(() => {
      const selected = options.find((opt) => opt.value == value);
      setSearchTerm(selected ? selected.label : "");
    }, [value, options]);

    const handleInputChange = (e) => {
      const term = e.target.value;
      setSearchTerm(term);
      setIsOpen(true);
    };

    const handleSelect = (selectedValue) => {
      setIsOpen(false);
      if (onChange) {
        onChange({ target: { name, value: selectedValue } });
      }
    };

    const displayValue = options.find((opt) => opt.value == value)?.label || "";
    const inputValue = isOpen ? searchTerm : displayValue;

    return (
      <div className="searchable-dropdown" ref={dropdownRef}>
        <div className={`input-group ${error ? "error" : ""}`}>
          <div className="input-icon">
            <i className="fas fa-search"></i>
          </div>
          <input
            type="text"
            className="form-input"
            placeholder={placeholder}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            disabled={disabled || loading}
            readOnly={!isOpen}
            required={required}
          />
          {isOpen && (
            <ul
              className="dropdown-list"
              style={{ width: "70%", textAlign: "left" }}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <li
                    style={{
                      padding: "5px",
                      margin: "2px",
                    }}
                    key={option.value}
                    className="dropdown-item"
                    onClick={() => handleSelect(option.value)}
                  >
                    {option.label}
                  </li>
                ))
              ) : (
                <li className="dropdown-item no-results">No results found</li>
              )}
            </ul>
          )}
        </div>
        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}
        {loading && <div className="loading-message">Loading...</div>}
      </div>
    );
  };

  // Form state
  const [formData, setFormData] = useState({
    service_reminder_customer_id: "",
    service_reminder_vehicle_id: "",
    service_reminder_date: "",
    service_reminder_status: "1", // 1=upcoming, 2=sent
    service_reminder_remark: "",
    service_reminder_grage_id: grage_id,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Dropdown options
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [customerVehicles, setCustomerVehicles] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);

  // Customer and vehicle data for existing reminder
  const [customerData, setCustomerData] = useState(null);
  const [vehicleData, setVehicleData] = useState(null);

  // Helper functions
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN");
  };

  const getStatusColor = (status) => {
    return status === "2" ? "#10b981" : "#3b82f6";
  };

  const getStatusText = (status) => {
    return status === "2" ? "Sent" : "Upcoming";
  };

  // Populate form if editing
  useEffect(() => {
    if (isEdit && existingReminder) {
      setFormData({
        service_reminder_customer_id:
          existingReminder.service_reminder_customer_id || "",
        service_reminder_vehicle_id:
          existingReminder.service_reminder_vehicle_id || "",
        service_reminder_date: existingReminder.service_reminder_date || "",
        service_reminder_status:
          existingReminder.service_reminder_status || "1",
        service_reminder_remark: existingReminder.service_reminder_remark || "",
        service_reminder_grage_id:
          existingReminder.service_reminder_grage_id || grage_id,
      });

      // Set customer and vehicle data from existing reminder
      if (existingReminder) {
        setCustomerData({
          customer_name: existingReminder.customer_name,
          customer_mobile: existingReminder.customer_mobile || "",
        });
        setVehicleData({
          vehicle_reg_no: existingReminder.vehicle_reg_no || "",
          brand_name: existingReminder.brand_name || "",
          model_name: existingReminder.model_name || "",
        });
      }
    } else {
      // Set default date to tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setFormData((prev) => ({
        ...prev,
        service_reminder_date: tomorrow.toISOString().split("T")[0],
        service_reminder_grage_id: grage_id,
      }));
    }
  }, [isEdit, existingReminder, grage_id]);

  // Fetch customers for this garage
  const fetchCustomers = async () => {
    setCustomersLoading(true);
    try {
      const formData = new FormData();
      formData.append("company_id", company_id);
      formData.append("customer_grage_id", grage_id);
      formData.append("searh_text", "");

      const response = await axios.post(`${server_url}customer.php`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.status === "1") {
        const customerOptions = (response.data.result || []).map(
          (customer) => ({
            value: customer.customer_id,
            label: `${customer.customer_name} (${
              customer.customer_mobile || "No Phone"
            })`,
          })
        );
        setCustomers(customerOptions);
      } else {
        setCustomers([]);
      }
    } catch (error) {
      console.error("Fetch customers error:", error);
      toast.error("Unable to fetch customers");
      setCustomers([]);
    } finally {
      setCustomersLoading(false);
    }
  };

  // Fetch vehicles for selected customer
  const fetchCustomerVehicles = async (customerId) => {
    if (!customerId) {
      setCustomerVehicles([]);
      return;
    }

    setVehiclesLoading(true);
    try {
      const formData = new FormData();
      formData.append("company_id", company_id);
      formData.append("vehicle_grage_id", grage_id);
      formData.append("vehicle_reg_no", "");
      formData.append("customer_id", customerId);

      const response = await axios.post(`${server_url}vehicle.php`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.status === "1") {
        const vehicleOptions = (response.data.result || []).map((vehicle) => ({
          value: vehicle.vehicle_id,
          label: `${vehicle.vehicle_reg_no} - ${vehicle.brand_name} ${vehicle.model_name}`,
        }));
        setCustomerVehicles(vehicleOptions);
      } else {
        setCustomerVehicles([]);
      }
    } catch (error) {
      console.error("Fetch customer vehicles error:", error);
      toast.error("Unable to fetch vehicles");
      setCustomerVehicles([]);
    } finally {
      setVehiclesLoading(false);
    }
  };

  // Fetch all vehicles for dropdown
  const fetchAllVehicles = async () => {
    setVehiclesLoading(true);
    try {
      const formData = new FormData();
      formData.append("company_id", company_id);
      formData.append("vehicle_grage_id", grage_id);
      formData.append("vehicle_reg_no", "");
      formData.append("customer_id", "");

      const response = await axios.post(`${server_url}vehicle.php`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.status === "1") {
        const vehicleOptions = (response.data.result || []).map((vehicle) => ({
          value: vehicle.vehicle_id,
          label: `${vehicle.vehicle_reg_no} - ${vehicle.brand_name} ${
            vehicle.model_name
          } (${vehicle.customer_name || "No Owner"})`,
        }));
        setVehicles(vehicleOptions);
      } else {
        setVehicles([]);
      }
    } catch (error) {
      console.error("Fetch vehicles error:", error);
      toast.error("Unable to fetch vehicles");
      setVehicles([]);
    } finally {
      setVehiclesLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
    if (!isEdit) {
      fetchAllVehicles();
    }
  }, []);

  // Handle customer selection
  useEffect(() => {
    if (formData.service_reminder_customer_id) {
      fetchCustomerVehicles(formData.service_reminder_customer_id);

      // Find and set customer data
      const selectedCustomer = customers.find(
        (c) => c.value == formData.service_reminder_customer_id
      );
      if (selectedCustomer) {
        // Extract customer info from label
        const match = selectedCustomer.label.match(/^(.*?) \((.*?)\)$/);
        if (match) {
          setCustomerData({
            customer_name: match[1] || "",
            customer_mobile: match[2] !== "No Phone" ? match[2] : "",
          });
        }
      }
    }
  }, [formData.service_reminder_customer_id, customers]);

  // Handle vehicle selection
  useEffect(() => {
    if (formData.service_reminder_vehicle_id) {
      let selectedVehicle;

      if (formData.service_reminder_customer_id) {
        // Look in customer vehicles
        selectedVehicle = customerVehicles.find(
          (v) => v.value == formData.service_reminder_vehicle_id
        );
      } else {
        // Look in all vehicles
        selectedVehicle = vehicles.find(
          (v) => v.value == formData.service_reminder_vehicle_id
        );
      }

      if (selectedVehicle) {
        // Extract vehicle info from label
        const match = selectedVehicle.label.match(/^(.*?) - (.*?)$/);
        if (match) {
          setVehicleData({
            vehicle_reg_no: match[1] || "",
            brand_model: match[2] || "",
          });
        }
      }
    }
  }, [formData.service_reminder_vehicle_id, customerVehicles, vehicles]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear customer when selecting from all vehicles dropdown
    if (
      name === "service_reminder_vehicle_id" &&
      !formData.service_reminder_customer_id
    ) {
      // Find the selected vehicle to get its customer
      const selectedVehicle = vehicles.find((v) => v.value == value);
      if (selectedVehicle) {
        // Extract customer from label if present
        const match = selectedVehicle.label.match(/\((.*?)\)$/);
        if (match && match[1] !== "No Owner") {
          // We could try to find the customer ID here, but for now just note it
          console.log("Selected vehicle belongs to:", match[1]);
        }
      }
    }

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.service_reminder_vehicle_id) {
      newErrors.service_reminder_vehicle_id = "Vehicle is required";
    }
    if (!formData.service_reminder_date) {
      newErrors.service_reminder_date = "Reminder date is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }
    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("company_id", company_id);
      formDataToSend.append(
        "service_reminder_customer_id",
        formData.service_reminder_customer_id
      );
      formDataToSend.append(
        "service_reminder_vehicle_id",
        formData.service_reminder_vehicle_id
      );
      formDataToSend.append(
        "service_reminder_date",
        formData.service_reminder_date
      );
      formDataToSend.append(
        "service_reminder_status",
        formData.service_reminder_status
      );
      formDataToSend.append(
        "service_reminder_remark",
        formData.service_reminder_remark
      );
      formDataToSend.append(
        "service_reminder_grage_id",
        formData.service_reminder_grage_id
      );

      // Add reminder_id if editing
      if (isEdit && existingReminder) {
        formDataToSend.append(
          "service_reminder_id",
          existingReminder.service_reminder_id
        );
      }

      const endpoint = isEdit
        ? "update_service_reminder.php"
        : "add_service_reminder.php";
      const response = await axios.post(
        `${server_url}${endpoint}`,
        formDataToSend,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.status == "1") {
        toast.success(
          response.data.error || "Service reminder saved successfully!"
        );
        navigate("/serviceReminderList");
      } else {
        toast.error(response.data.error || "Failed to save service reminder");
      }
    } catch (error) {
      console.error("Save service reminder error:", error);
      toast.error("Unable to save service reminder. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // If no existing data in edit mode
  if (isEdit && !existingReminder) {
    return (
      <div className="login-container">
        <div className="login-form-container">
          <div style={{ textAlign: "center", padding: "40px" }}>
            <h3>No service reminder data found</h3>
            <p>Please go back and select a reminder to edit.</p>
            <Link
              to="/service-reminders"
              style={{ color: "#667eea", textDecoration: "none" }}
            >
              <i className="fas fa-arrow-left"></i> Back to Service Reminders
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-form-container">
        <div className="login-form-wrapper">
          {/* Reminder Info Section (for edit mode only) */}
          {isEdit && existingReminder && (
            <div
              className="reminder-info-card"
              style={{
                marginBottom: "20px",
                backgroundColor: "#f8f9fa",
                borderRadius: "12px",
                padding: "15px",
                borderLeft: `4px solid ${getStatusColor(
                  existingReminder.service_reminder_status
                )}`,
              }}
            >
              <div
                className="reminder-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <div
                  className="vehicle-reg"
                  style={{
                    fontWeight: "bold",
                    fontSize: "16px",
                    color: "#2d3748",
                  }}
                >
                  {existingReminder.vehicle_reg_no || ""}
                </div>
                <div
                  className="reminder-status"
                  style={{
                    color: getStatusColor(
                      existingReminder.service_reminder_status
                    ),
                    fontSize: "12px",
                    fontWeight: "500",
                  }}
                >
                  {getStatusText(existingReminder.service_reminder_status)}
                </div>
              </div>
              <div
                className="reminder-details"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <div
                  className="customer-info"
                  style={{ fontSize: "14px", color: "#4a5568" }}
                >
                  <i
                    className="fas fa-user"
                    style={{ marginRight: "5px", color: "#4a5568" }}
                  ></i>
                  {customerData?.customer_name ||
                    existingReminder.customer_name ||
                    "N/A"}
                  {customerData?.customer_mobile ||
                  existingReminder.customer_mobile ? (
                    <a
                      style={{
                        marginLeft: "5px",
                        textDecoration: "underline",
                      }}
                      href={`tel:${
                        customerData?.customer_mobile ||
                        existingReminder.customer_mobile
                      }`}
                    >
                      (
                      {customerData?.customer_mobile ||
                        existingReminder.customer_mobile}
                      )
                    </a>
                  ) : (
                    ""
                  )}
                </div>
                <div
                  className="reminder-date"
                  style={{
                    fontSize: "14px",
                    color: "#4a5568",
                  }}
                >
                  <i
                    className="fas fa-calendar-day"
                    style={{ marginRight: "5px" }}
                  ></i>
                  Reminder Date:{" "}
                  {formatDate(existingReminder.service_reminder_date)}
                </div>
                {existingReminder.service_reminder_remark && (
                  <div
                    className="reminder-remark"
                    style={{
                      fontSize: "14px",
                      color: "#4a5568",
                      fontStyle: "italic",
                      paddingTop: "5px",
                      borderTop: "1px solid #e2e8f0",
                    }}
                  >
                    <i
                      className="fas fa-sticky-note"
                      style={{ marginRight: "5px" }}
                    ></i>
                    {existingReminder.service_reminder_remark}
                  </div>
                )}
              </div>
            </div>
          )}

          <form className="login-form" onSubmit={handleSubmit}>
            {/* Customer Selection */}
            <div className="form-group">
              <div
                style={{ display: "-webkit-inline-box", marginBottom: "0px" }}
              >
                <span
                  style={{
                    textAlign: "left",
                    display: "block",
                    marginBottom: "0px",
                    fontWeight: "bold",
                    width: "50%",
                  }}
                >
                  Customer
                </span>{" "}
                <span
                  style={{
                    textAlign: "right",
                    display: "block",
                    marginBottom: "0px",
                    fontWeight: "bold",
                    width: "50%",
                  }}
                >
                  <Link
                    to="/add-customer"
                    style={{ textDecoration: "underline" }}
                  >
                    Add Customer
                  </Link>
                </span>
              </div>
              <SearchableDropdown
                name="service_reminder_customer_id"
                options={customers}
                value={formData.service_reminder_customer_id}
                onChange={handleChange}
                placeholder="Select Customer (Optional)"
                error={errors.service_reminder_customer_id}
                loading={customersLoading}
              />
            </div>

            {/* Vehicle Selection - Show customer vehicles if customer selected, else all vehicles */}
            <div className="form-group">
              <div
                style={{ display: "-webkit-inline-box", marginBottom: "0px" }}
              >
                <span
                  style={{
                    textAlign: "left",
                    display: "block",
                    marginBottom: "0px",
                    fontWeight: "bold",
                    width: "50%",
                  }}
                >
                  Vehicle *
                </span>{" "}
                <span
                  style={{
                    textAlign: "right",
                    display: "block",
                    marginBottom: "0px",
                    fontWeight: "bold",
                    width: "50%",
                  }}
                >
                  {!formData.service_reminder_customer_id && (
                    <Link
                      to="/add-customer-vehicle"
                      style={{ textDecoration: "underline" }}
                    >
                      Add Vehicle
                    </Link>
                  )}
                </span>
              </div>
              <SearchableDropdown
                name="service_reminder_vehicle_id"
                options={
                  formData.service_reminder_customer_id
                    ? customerVehicles
                    : vehicles
                }
                value={formData.service_reminder_vehicle_id}
                onChange={handleChange}
                placeholder={
                  formData.service_reminder_customer_id
                    ? "Select Customer's Vehicle"
                    : "Select Vehicle"
                }
                error={errors.service_reminder_vehicle_id}
                loading={vehiclesLoading}
                required
              />
            </div>

            {/* Reminder Date */}
            <div className="form-group">
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "0px",
                  fontWeight: "bold",
                }}
              >
                Reminder Date *
              </label>
              <div className="input-group">
                <div className="input-icon">
                  <i className="fas fa-calendar-day"></i>
                </div>
                <input
                  type="date"
                  name="service_reminder_date"
                  className={`form-input ${
                    errors.service_reminder_date ? "error" : ""
                  }`}
                  value={formData.service_reminder_date}
                  onChange={handleChange}
                  required
                  min={new Date().toISOString().split("T")[0]} // Can't set reminder for past dates
                />
              </div>
              {errors.service_reminder_date && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i>
                  {errors.service_reminder_date}
                </div>
              )}
            </div>

            {/* Status (only in edit mode) */}
            {isEdit && (
              <div className="form-group">
                <label
                  style={{
                    textAlign: "left",
                    display: "block",
                    marginBottom: "0px",
                    fontWeight: "bold",
                  }}
                >
                  Status
                </label>
                <div
                  className="radio-group"
                  style={{
                    display: "-webkit-box",
                    gap: "10px",
                    marginTop: "10px",
                  }}
                >
                  <div
                    className="radio-option"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <input
                      type="radio"
                      id="status_upcoming"
                      name="service_reminder_status"
                      value="1"
                      checked={formData.service_reminder_status === "1"}
                      onChange={handleChange}
                      style={{ marginRight: "4px" }}
                    />
                    <label
                      htmlFor="status_upcoming"
                      style={{ cursor: "pointer", marginBottom: "0px" }}
                    >
                      Upcoming
                    </label>
                  </div>
                  <div
                    className="radio-option"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <input
                      type="radio"
                      id="status_sent"
                      name="service_reminder_status"
                      value="2"
                      checked={formData.service_reminder_status === "2"}
                      onChange={handleChange}
                      style={{ marginRight: "4px" }}
                    />
                    <label
                      htmlFor="status_sent"
                      style={{ cursor: "pointer", marginBottom: "0px" }}
                    >
                      Sent
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Remarks */}
            <div className="form-group">
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                }}
              >
                Remarks
              </label>
              <div className="input-group">
                <div className="input-icon">
                  <i className="fas fa-sticky-note"></i>
                </div>
                <textarea
                  name="service_reminder_remark"
                  className="form-input textarea"
                  placeholder="Add any remarks or notes for this reminder..."
                  value={formData.service_reminder_remark}
                  onChange={handleChange}
                  rows="3"
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="login-button"
              disabled={submitting || customersLoading || vehiclesLoading}
            >
              <i className="fas fa-save"></i>
              {submitting
                ? "Saving..."
                : isEdit
                ? "Update Reminder"
                : "Add Reminder"}
            </button>
          </form>
        </div>
      </div>
      <style jsx>{`
        .content-area {
          padding-top: 110px;
        }
        .login-container {
          padding: 15px;
          min-height: 60vh;
          background: #f8f9fa;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .login-form-container {
          width: 100%;
          max-width: 600px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }
        @media (max-width: 768px) {
          .login-form-container {
            flex: none;
            padding: 0px;
          }
        }
        .login-form-wrapper {
          padding: 20px;
        }
        .login-form {
          display: flex;
          flex-direction: column;
          gap: 0px;
        }
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        .form-row {
          display: flex;
          gap: 10px;
        }
        label {
          font-weight: 600;
          color: #4a5568;
          font-size: 14px;
        }
        .input-group {
          position: relative;
          display: flex;
          align-items: center;
        }
        .input-icon {
          position: absolute;
          left: 12px;
          color: #a0aec0;
          z-index: 1;
          font-size: 16px;
        }
        .form-input {
          width: 100%;
          padding: 10px 12px 10px 40px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          transition: all 0.3s;
          background: white;
        }
        .form-input:focus {
          outline: none;
          border-color: #0352a1;
          box-shadow: 0 0 0 3px rgba(3, 82, 161, 0.1);
        }
        .form-input.error {
          border-color: #dc2626;
        }
        .form-input.textarea {
          min-height: 80px;
          resize: vertical;
          padding-top: 10px;
          padding-bottom: 10px;
        }
        /* Searchable Dropdown */
        .searchable-dropdown {
          position: relative;
        }
        .dropdown-list {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          max-height: 200px;
          overflow-y: auto;
          z-index: 1000;
          margin-top: 2px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        .dropdown-item {
          padding: 8px 12px;
          cursor: pointer;
          transition: background 0.2s;
          font-size: 14px;
        }
        .dropdown-item:hover {
          background: #f7fafc;
        }
        .dropdown-item.no-results {
          color: #a0aec0;
          text-align: center;
        }
        /* Radio Group */
        .radio-group {
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
          margin-top: 5px;
        }
        .radio-option {
          display: flex;
          align-items: center;
          cursor: pointer;
        }
        .radio-option input[type="radio"] {
          width: 18px;
          height: 18px;
          margin-right: 8px;
          cursor: pointer;
        }
        .radio-option label {
          cursor: pointer;
          user-select: none;
          font-size: 14px;
          color: #333;
        }
        /* Error Message */
        .error-message {
          color: #dc2626;
          font-size: 12px;
          margin-top: 4px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        /* Loading Message */
        .loading-message {
          color: #666;
          font-size: 12px;
          margin-top: 4px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        /* Button Styles */
        .login-button {
          background: #0352a1;
          color: white;
          border: none;
          padding: 12px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-top: 10px;
        }
        .login-button:hover:not(:disabled) {
          background: #024080;
        }
        .login-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        @media (max-width: 768px) {
          .login-form-wrapper {
            padding: 15px;
          }
          .form-row {
            flex-direction: column;
            gap: 0px;
          }
          .radio-group {
            flex-direction: column;
            gap: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default AddServiceReminder;
